import { supabase } from "@/integrations/supabase/client";
import { fingerprintError, redactText, redactUrlLike } from "@/lib/errorRedaction";
import { BUILD_COMMIT, BUILD_TIME, APP_VERSION, RELEASE } from "@/lib/buildInfo";

/**
 * Frontend runtime error logging.
 *
 * Captures uncaught errors + unhandled promise rejections (the class of crash
 * that produced `HelmetDispatcher.init` blank screens) and persists them to
 * `public.frontend_error_events` so admins can triage them in the app.
 *
 * Every field is redacted (emails, tokens, ids, query values) before it leaves
 * the browser, and every row is tagged with a deduped stack fingerprint plus
 * the build/release it came from so crash rates are comparable per deployment.
 *
 * Production bundles are built with `sourcemap: true`, so the file/line/column
 * recorded here resolves back to original source in devtools or any
 * source-map-aware viewer.
 */

export type FrontendErrorType = "error" | "unhandledrejection" | "react";

export interface FrontendErrorPayload {
  errorType: FrontendErrorType;
  message: string;
  source?: string | null;
  lineno?: number | null;
  colno?: number | null;
  stack?: string | null;
  route?: string;
}

const SESSION_KEY = "td_error_session_id";
const MAX_EVENTS_PER_SESSION = 20;
const DEDUPE_WINDOW_MS = 60_000;

let sentCount = 0;
const recentlySent = new Map<string, number>();
let installed = false;

function truncate(value: string | null | undefined, max: number): string | null {
  if (!value) return null;
  return value.length > max ? value.slice(0, max) : value;
}

function sessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return "no-storage";
  }
}

export async function logFrontendError(payload: FrontendErrorPayload): Promise<boolean> {
  if (import.meta.env.VITE_ERROR_LOGGING_DISABLED === "1") return false;
  if (sentCount >= MAX_EVENTS_PER_SESSION) return false;

  // Redact first, then fingerprint, so grouping never depends on secrets.
  const message = redactText(payload.message) || "Unknown error";
  const stack = redactText(payload.stack);
  const source = redactUrlLike(payload.source);
  const fingerprint = fingerprintError({
    errorType: payload.errorType,
    message,
    source,
    stack,
  });

  const now = Date.now();
  const last = recentlySent.get(fingerprint);
  if (last && now - last < DEDUPE_WINDOW_MS) return false;
  recentlySent.set(fingerprint, now);
  sentCount += 1;

  try {
    const { data } = await supabase.auth.getSession();
    const row = {
      error_type: payload.errorType,
      message: truncate(message, 2000) as string,
      route: truncate(redactUrlLike(payload.route ?? window.location.pathname), 300) ?? "/",
      source: truncate(source, 500),
      lineno: payload.lineno ?? null,
      colno: payload.colno ?? null,
      stack: truncate(stack, 8000),
      fingerprint,
      release: truncate(RELEASE, 100),
      app_version: truncate(APP_VERSION, 64),
      build_commit: truncate(BUILD_COMMIT, 64),
      build_time: BUILD_TIME,
      user_agent: truncate(redactText(navigator.userAgent), 500),
      session_id: truncate(sessionId(), 100),
      user_id: data.session?.user.id ?? null,
    };
    const { error } = await supabase.from("frontend_error_events").insert(row);
    return !error;
  } catch {
    // Never let the logger throw — it runs inside error handlers.
    return false;
  }
}

/** Attach global handlers once. Safe to call multiple times. */
export function installErrorLogging(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (event: ErrorEvent) => {
    // Resource load failures have no `error` object and a target element.
    if (!event.error && event.target && event.target !== window) return;
    void logFrontendError({
      errorType: "error",
      message: event.message || String(event.error ?? "Unknown error"),
      source: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error instanceof Error ? event.error.stack : null,
    });
  });

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    void logFrontendError({
      errorType: "unhandledrejection",
      message: reason instanceof Error ? reason.message : String(reason),
      stack: reason instanceof Error ? reason.stack : null,
    });
  });
}

/** Test hook: reset in-memory throttling state. */
export function __resetErrorLoggingState(): void {
  sentCount = 0;
  recentlySent.clear();
}
